
import { supabase } from '@/integrations/supabase/client';
import { debugLogger } from '@/utils/debugLogger';

interface CachedSignedUrl {
  url: string;
  expiresAt: number;
}

// Cache for signed URLs to avoid repeated requests
const signedUrlCache = new Map<string, CachedSignedUrl>();

// Default expiration time: 24 hours (increased from 2 hours)
const DEFAULT_EXPIRATION_SECONDS = 24 * 60 * 60;

/**
 * Extract bucket name from a full Supabase storage URL
 */
const extractBucketFromUrl = (url: string): string | null => {
  const match = url.match(/\/storage\/v1\/object\/(?:public|sign)\/([^/]+)\//);
  return match ? match[1] : null;
};

/**
 * Determine the correct bucket for a given media URL
 */
const determineBucket = (mediaUrl: string): string => {
  // Extract bucket from URL if present
  const bucketFromUrl = extractBucketFromUrl(mediaUrl);
  if (bucketFromUrl) {
    debugLogger.debug('[SignedMedia] Bucket extracted from URL:', bucketFromUrl);
    return bucketFromUrl;
  }
  
  // Detect bucket from full URLs also
  if (mediaUrl.includes('/chat_media/')) return 'chat_media';
  if (mediaUrl.includes('/verification-documents/')) return 'verification-documents';
  if (mediaUrl.includes('/profile_photos/')) return 'profile_photos';
  
  // Detect by path prefix (direct file paths)
  if (mediaUrl.startsWith('verification-documents/')) return 'verification-documents';
  if (mediaUrl.startsWith('conversations/')) return 'chat_media';
  if (mediaUrl.startsWith('images/')) return 'chat_media';
  if (mediaUrl.startsWith('audio/')) return 'chat_media';
  
  // Default: chat_media
  debugLogger.debug('[SignedMedia] Using default bucket: chat_media');
  return 'chat_media';
};

/**
 * Get a signed URL for a chat media file with enhanced validation
 * @param filePath - The file path in the bucket
 * @param expirationSeconds - URL expiration time in seconds (default: 24 hours)
 * @returns Promise<string | null> - The signed URL or null if failed
 */
export const getSignedMediaUrl = async (
  filePath: string, 
  expirationSeconds: number = DEFAULT_EXPIRATION_SECONDS,
  bucketName?: string
): Promise<string | null> => {
  try {
    if (!filePath || filePath.trim().length === 0) {
      debugLogger.error('[SignedMedia] Invalid file path provided');
      return null;
    }

    // Check cache first
    const cached = signedUrlCache.get(filePath);
    if (cached && cached.expiresAt > Date.now()) {
      debugLogger.debug('[SignedMedia] Using cached URL for:', filePath);
      return cached.url;
    }

    debugLogger.debug('[SignedMedia] Generating new signed URL for:', filePath);

    // Generate new signed URL with retry logic
    const maxRetries = 3;
    let lastError: any = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const bucket = bucketName || determineBucket(filePath);
        debugLogger.debug(`[SignedMedia] Using bucket: ${bucket} (explicit: ${!!bucketName}) for path: ${filePath}`);
        
        const { data, error } = await supabase.storage
          .from(bucket)
          .createSignedUrl(filePath, expirationSeconds, {
            transform: {
              width: undefined,
              height: undefined,
              resize: undefined,
              format: undefined,
              quality: undefined
            }
          });

      if (error) {
        // ✅ FASE 3: Detectar "Object not found" e não fazer retry desnecessário
        if (error.message?.includes('Object not found')) {
          debugLogger.error('[SignedMedia] File does not exist in storage:', {
            bucket: bucketName,
            path: filePath,
            hint: 'File was never uploaded or has been deleted'
          });
          throw new Error(`MediaNotFound: ${filePath}`);
        }
        
        // Para outros erros (rede, permissão), continuar retry
        debugLogger.error('[SignedMedia] Supabase error on attempt', attempt, ':', error);
        lastError = error;
        
        // Retry with exponential backoff
        if (attempt < maxRetries) {
          debugLogger.warn('[SignedMedia] Retrying in', attempt * 500, 'ms...');
          await new Promise(resolve => setTimeout(resolve, attempt * 500));
          continue;
        }
      }

        if (!data?.signedUrl) {
          debugLogger.error('[SignedMedia] No signed URL returned on attempt', attempt);
          lastError = new Error('No signed URL returned');
          
          if (attempt < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, attempt * 500));
            continue;
          }
          throw lastError;
        }

        // Validate the generated URL
        if (!validateSignedUrl(data.signedUrl)) {
          debugLogger.error('[SignedMedia] Generated URL failed validation on attempt', attempt);
          lastError = new Error('Generated URL failed validation');
          
          if (attempt < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, attempt * 500));
            continue;
          }
          throw lastError;
        }

        // Cache the URL (expires 1 hour before actual expiration for safety)
        const expiresAt = Date.now() + (expirationSeconds - 3600) * 1000;
        signedUrlCache.set(filePath, {
          url: data.signedUrl,
          expiresAt
        });

        debugLogger.debug('[SignedMedia] Successfully generated signed URL:', {
          filePath,
          urlLength: data.signedUrl.length,
          hasToken: data.signedUrl.includes('token='),
          attempt,
          expiresInHours: expirationSeconds / 3600
        });

        return data.signedUrl;
      } catch (error) {
        lastError = error;
        debugLogger.warn('[SignedMedia] Attempt', attempt, 'failed:', error);
      }
    }

    debugLogger.error('[SignedMedia] All attempts failed:', lastError);
    return null;
  } catch (error) {
    debugLogger.error('[SignedMedia] Error getting signed URL:', error);
    return null;
  }
};

/**
 * Validate signed URL format and completeness
 */
function validateSignedUrl(url: string): boolean {
  try {
    if (!url || url.length < 100) {
      debugLogger.warn('[SignedMedia] URL too short:', url?.length || 0);
      return false;
    }

    if (!url.includes('token=')) {
      debugLogger.warn('[SignedMedia] URL missing token parameter');
      return false;
    }

    // Allow chat_media, verification-documents, and profile_photos buckets
    if (!url.includes('chat_media') && 
        !url.includes('verification-documents') && 
        !url.includes('profile_photos')) {
      debugLogger.warn('[SignedMedia] URL missing bucket reference');
      return false;
    }

    // Validate URL can be parsed
    const urlObj = new URL(url);
    if (!urlObj.hostname || !urlObj.pathname) {
      debugLogger.warn('[SignedMedia] URL has invalid format');
      return false;
    }

    return true;
  } catch (error) {
    debugLogger.warn('[SignedMedia] URL validation error:', error);
    return false;
  }
}

// Legacy alias for backwards compatibility
export const getSignedURL = getSignedMediaUrl;

/**
 * Extract file path from public URL with enhanced validation
 * @param publicUrl - The public URL from storage
 * @returns string | null - The file path or null if invalid
 */
export const extractFilePathFromUrl = (publicUrl: string): string | null => {
  try {
    if (!publicUrl || publicUrl.trim().length === 0) {
      debugLogger.error('[SignedMedia] Empty or null URL provided');
      return null;
    }

    const url = new URL(publicUrl);
    const pathParts = url.pathname.split('/');
    
    // Find bucket name in path (chat_media or verification-documents)
    const bucketIndex = pathParts.findIndex(part => 
      part === 'chat_media' || part === 'verification-documents'
    );
    
    if (bucketIndex === -1 || bucketIndex === pathParts.length - 1) {
      debugLogger.warn('[SignedMedia] Bucket not found in URL path:', publicUrl);
      
      // Fallback - try to extract path after /public/ or /sign/
      const publicIndex = pathParts.findIndex(part => part === 'public' || part === 'sign');
      if (publicIndex !== -1 && publicIndex < pathParts.length - 2) {
        // Get bucket + path after /public/ or /sign/
        const filePath = pathParts.slice(publicIndex + 2).join('/');
        debugLogger.debug('[SignedMedia] Extracted file path via fallback:', filePath);
        return filePath || null;
      }
      
      return null;
    }
    
    const filePath = pathParts.slice(bucketIndex + 1).join('/');
    
    if (!filePath || filePath.trim().length === 0) {
      debugLogger.warn('[SignedMedia] No file path found after bucket name');
      return null;
    }

    debugLogger.debug('[SignedMedia] Extracted file path:', filePath);
    return filePath;
  } catch (error) {
    debugLogger.error('[SignedMedia] Error extracting file path:', error);
    return null;
  }
};

/**
 * Convert public URL to signed URL with enhanced error handling
 * @param publicUrl - The public URL from storage
 * @param expirationSeconds - URL expiration time in seconds
 * @returns Promise<string | null> - The signed URL or original URL if failed
 */
export const convertToSignedUrl = async (
  publicUrl: string, 
  expirationSeconds: number = DEFAULT_EXPIRATION_SECONDS
): Promise<string | null> => {
  const filePath = extractFilePathFromUrl(publicUrl);
  if (!filePath) {
    debugLogger.warn('[SignedMedia] Could not extract file path from URL:', publicUrl);
    return publicUrl; // Fallback to original URL
  }
  
  const signedUrl = await getSignedMediaUrl(filePath, expirationSeconds);
  return signedUrl || publicUrl; // Fallback to original URL if signing fails
};

/**
 * Clear expired URLs from cache
 */
export const clearExpiredCache = (): void => {
  const now = Date.now();
  let cleared = 0;
  
  for (const [key, value] of signedUrlCache.entries()) {
    if (value.expiresAt <= now) {
      signedUrlCache.delete(key);
      cleared++;
    }
  }
  
  if (cleared > 0) {
    debugLogger.debug('[SignedMedia] Cleared', cleared, 'expired cache entries');
  }
};

/**
 * Clear all cached URLs (useful when leaving a conversation)
 */
export const clearAllCache = (): void => {
  const size = signedUrlCache.size;
  signedUrlCache.clear();
  debugLogger.debug('[SignedMedia] Cleared all cache entries:', size);
};

// Auto-cleanup expired entries every 5 minutes (disabled during tests)
const IS_TEST =
  (typeof process !== 'undefined' && (process.env.VITEST === 'true' || process.env.NODE_ENV === 'test')) ||
  (typeof import.meta !== 'undefined' && (import.meta as any).env?.MODE === 'test') ||
  (typeof window !== 'undefined' && (window as any).__VITEST_BROWSER__ === true);

if (!IS_TEST) {
  setInterval(clearExpiredCache, 5 * 60 * 1000);
}
